import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "facebook",
  app: "Facebook",
  title: "How to block Facebook on iPhone but keep the useful parts",
  h1: "How to block Facebook",
  description:
    "Screen Time steps, why Facebook is four services in one icon, and how to leave the feed without losing the group your street organises on.",
  targets: [
    "how to block facebook on iphone",
    "block facebook app",
    "how to quit facebook feed",
    "facebook screen time limit",
  ],
  published: "2026-09-03",
  standfirst:
    "Almost nobody opens Facebook to read the feed any more. They open it for an event, a group or Marketplace, and the feed is what they get first.",
  takeaways: [
    "The reason people cannot leave is that Facebook holds infrastructure — the school group, the street group, the club — that has no other home.",
    "The feed is the tax you pay to reach that infrastructure, and it is charged on every visit.",
    "Blocking the app and using the browser is the version that works, because it separates the errand from the feed.",
  ],
  mechanic: [
    "Facebook is four services in one icon, and only one of them is the problem. There is a feed almost nobody says they enjoy. There are Groups, which for a great many people are where a school year, a street, a hobby or a church actually organises. There is Marketplace, which has quietly become the largest second-hand market in most countries. And there are Events, which is still where a surprising number of things get arranged.",
    "The three useful ones are what keep people installed, and the feed knows it. Every path to a group, an event or a listing goes through it, so a visit to check one thing is charged an unpredictable amount of scrolling on the way in and on the way out. The errand is real. The tax is the product.",
    "Facebook's notifications compound this by being deliberately ambiguous: a notification that somebody posted in a group you are in looks identical to one that matters, and resolving it puts you back at the top of the feed. It is a system that converts your genuine social obligations into feed sessions, which is a considerably cleverer design than an infinite scroll.",
  ],
  limitFails:
    "The limit fires and you dismiss it, because the thing you opened Facebook for was real — the event details, the message from the group, the item you were collecting. That is the specific pattern here: Facebook has enough legitimate use to generate a legitimate exception nearly every time the limit fires, and after the third dismissal the limit has taught you that dismissing is what you do. A time budget cannot distinguish between two minutes of Marketplace and forty of feed, and the two arrive through the same door.",
  instead: [
    "Delete the app and use the browser. The mobile site is worse in every way that matters, and the errands still work.",
    "Turn off all notifications except messages and event reminders. Group-activity notifications are the main feed-delivery mechanism.",
    "Unfollow — not unfriend — everyone whose posts you do not actually want. It empties the feed while leaving every group and connection intact.",
    "For Marketplace specifically, save the direct URL and go straight there rather than through the home page.",
  ],
  verses: [
    {
      verse: {
        text: "Therefore watch carefully how you walk, not as unwise, but as wise, redeeming the time, because the days are evil.",
        ref: "Ephesians 5:15–16",
      },
      note: "Redeeming is a commercial word — buying back something already pledged, at a price. That is a precise description of what it takes to get an hour out of an evening that has been designed to absorb it, and the passage assumes the buying back is deliberate and costs something rather than happening by good intentions.",
    },
    {
      verse: {
        text: "Sheol and Abaddon are never satisfied; and a man's eyes are never satisfied.",
        ref: "Proverbs 27:20",
      },
      note: "An observation about capacity rather than morality: there is no quantity of looking that produces the state of having looked enough. Three thousand years before an infinite feed, and the mechanism it identifies — that the appetite is not the sort of thing satisfaction applies to — is exactly the one being farmed.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Unfollow everyone",
      id: "unfollow-everyone",
    },
    {
      type: "p",
      text: "The most underused control on Facebook is Unfollow, which keeps the connection and removes the posts. Done thoroughly it produces a nearly empty feed while leaving every group, event and message untouched.",
    },
    {
      type: "p",
      text: "It takes about twenty minutes once and it is the single highest-leverage thing on this page, because it removes the tax rather than the errand.",
    },
    {
      type: "h2",
      text: "The browser is the answer here",
      id: "the-browser-is-the-answer-here",
    },
    {
      type: "p",
      text: "For an app whose value is errands, moving to the mobile website is nearly costless. The errands work; the app's notification hooks and its smoothness do not follow you.",
    },
    {
      type: "note",
      text: "Keep Messenger if you need it — it is a separate app, and separating the messages from the feed is most of the win.",
    },
    {
      type: "h2",
      text: "What you are actually protecting",
      id: "what-you-are-actually-protecting",
    },
    {
      type: "p",
      text: "The reason to be careful with Facebook rather than absolutist about it is that the groups are frequently real community — a street, a school year, a support group — and leaving them is a genuine social cost that people who advise deleting everything tend not to price in.",
    },
    {
      type: "p",
      text: "Keep the community, lose the feed. That distinction is available here in a way it is not on most of {these pages}.",
      links: {
        "these pages": "/block",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Facebook on my iPhone?",
      a: "Screen Time → App Limits → Social Networking with Block at End of Limit and a passcode. But for Facebook specifically, deleting the app and using the browser works better, because the errands survive and the feed's hooks do not.",
    },
    {
      q: "How do I use Facebook groups without the feed?",
      a: "Unfollow everyone — it keeps the connections and empties the feed — and use the browser rather than the app. Groups, Events and Marketplace all work fine there.",
    },
    {
      q: "Should I delete my Facebook account?",
      a: "For most people the account is worth keeping and the app is not. The account holds groups, events and Marketplace access that frequently have no alternative; the app is what charges you a feed session to reach them.",
    },
  ],
  relatedApps: ["instagram", "twitter"],
  related: ["social-media-fast-guide", "screen-time-limits-dont-work"],
  relatedPrayers: [
    "prayer-for-comparison",
    "prayer-before-you-open-your-phone",
  ],
};
